import { useMemo } from 'react';
import styled from 'styled-components';

import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { findIndex, get, map } from 'lodash';
import PropTypes from 'prop-types';
import { Route, withRouter } from 'react-router-dom';

import useDimensions from '../../helpers/hooks/useDimensions';

import { Text } from '../typography';
import LoadingBars from '../LoadingBars';

import { COLORS, MobileSize } from '../../helpers/constants/styleguide';
import { hexToRgba } from '../../helpers/utils/common';

const buildUrl = (baseURL, tabURL) => {
  let URL = '';
  if (baseURL) {
    URL = baseURL;
  }
  if (tabURL) {
    URL = `${URL}${tabURL}`;
  }
  return URL;
};

/**
 * `history` and `location` are injected by withRouter; consumers do not pass
 * them. No rest spread: other props are dropped rather than forwarded.
 *
 * @typedef {Object} TabGroupOwnProps
 * @property {Array<{ title: string, content: React.ReactElement, url?: string, loading?: boolean, footer?: React.ReactNode, disabled?: boolean }>} tabs
 * @property {string} [baseUrl]
 * @property {string} [defaultPath]
 * @property {boolean} [transparent]
 */

/**
 * @typedef {TabGroupOwnProps & import('react-router-dom').RouteComponentProps} TabGroupProps
 */

/** @type {React.FC<TabGroupProps>} */
const TabGroup = ({
  tabs,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  baseUrl,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  history,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  location,
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  defaultPath,
  transparent = false,
}) => {
  const [tabListRef, { height: headerHeight }] = useDimensions();
  const handleTabSelect = tabIndex => {
    if (!tabs[tabIndex].disabled) {
      history.push(buildUrl(baseUrl, tabs[tabIndex].url));
    }
  };

  const getSelectedTabIndex = useMemo(() => {
    const { pathname } = location;

    const selectedIndex = findIndex(tabs, tab => {
      return pathname === buildUrl(baseUrl, tab.url);
    });

    if (selectedIndex === -1 && defaultPath) {
      const defaultUrl = buildUrl(baseUrl, defaultPath);
      const index = findIndex(tabs, () => pathname === defaultUrl);

      history.push(defaultUrl);
      return index;
    }

    if (!get(tabs[selectedIndex], 'disabled', false)) {
      return selectedIndex;
    }

    const rootIndex = findIndex(tabs, tab => {
      return buildUrl(baseUrl, tab.url) === buildUrl(baseUrl);
    });

    history.push(buildUrl(baseUrl));

    return rootIndex;
  }, [location, tabs, baseUrl, history, defaultPath]);

  return (
    <TabsContainer
      selectedIndex={getSelectedTabIndex}
      onSelect={handleTabSelect}
    >
      <TabListBackgroundWrapper>
        <TabListContainer ref={tabListRef}>
          <StyledTabList id="styled-tab-list">
            {map(tabs, ({ title, disabled = false }) => (
              <StyledTab
                key={title}
                className={tabs.length === 1 ? '' : 'multi-tab'}
                disabled={disabled}
                data-testid={`tab-${title}`}
              >
                <Text color="secondary" bold>
                  {title}
                </Text>
              </StyledTab>
            ))}
          </StyledTabList>
        </TabListContainer>
      </TabListBackgroundWrapper>

      <TabPanelContainer headerHeight={headerHeight}>
        {map(tabs, ({ content, loading, url }, index) => {
          return (
            <StyledTabPanel key={index} $transparent={transparent}>
              <Route
                exact
                path={buildUrl(baseUrl, url)}
                render={() => (
                  <>
                    {loading ? (
                      <LoadingBarsContainer>
                        <LoadingBars color={COLORS.azure} />
                      </LoadingBarsContainer>
                    ) : (
                      <>
                        <TabContentContainer>{content}</TabContentContainer>
                      </>
                    )}
                  </>
                )}
              />
            </StyledTabPanel>
          );
        })}
      </TabPanelContainer>
    </TabsContainer>
  );
};

TabGroup.propTypes = {
  /** Takes an array of tabs with the following fields: {
   * title: string,
   * content: component,
   * footer: component (optional)
   * } */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.element.isRequired,
    }),
  ).isRequired,
  transparent: PropTypes.bool,
};

const TabsContainer = styled(Tabs)`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  box-shadow: 0 2px 4px 0 ${hexToRgba(COLORS.grey, 0.9)};
`;

const TabListBackgroundWrapper = styled.div`
  width: 100%;
  background-color: ${COLORS.white};
`;

const TabListContainer = styled.div`
  margin: 0 auto;
  width: 100%;
`;

const StyledTabList = styled(TabList)`
  display: flex;
  justify-content: ${({ children }) =>
    children?.length > 1 ? 'center' : 'stretch'};
  margin: 0 auto;
  width: 100%;
`;

const StyledTab = styled(Tab)`
  &.multi-tab:hover {
    ${({ disabled }) => (disabled ? '' : 'opacity: 0.8;')}
  }

  border-bottom: 3px solid ${COLORS.grey};

  &.react-tabs__tab--selected {
    border-bottom: 3px solid ${COLORS.azure};

    div {
      color: ${COLORS.azure};
    }
  }

  list-style: none;
  width: ${({ className }) => (className === 'multi-tab' ? '50%' : '100%')};
  padding: 16px 16px 12px;
  text-align: center;

  &.multi-tab {
    ${({ disabled }) => (disabled ? '' : 'cursor: pointer;')}
  }
`;

const TabPanelContainer = styled.section`
  flex: 1;
`;

const StyledTabPanel = styled(TabPanel)`
  display: flex;
  flex-direction: column;
  &.react-tabs__tab-panel--selected {
    height: 100%;
  }
  position: relative;
  background-color: ${({ $transparent }) =>
    $transparent ? 'transparent' : COLORS.white};
`;

const TabContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding-top: 16px;
  padding-bottom: 32px;
  @media (max-width: ${MobileSize}) {
    padding-bottom: 8px;
  }
`;

const LoadingBarsContainer = styled.div`
  margin: 240px auto;
`;

export default withRouter(TabGroup);
